import type { FC, SVGProps } from 'react'
import { ReactComponent as BranchLogoArt } from '../assets/brand/branch-logo.svg'
import { ReactComponent as LeafLogoArt } from '../assets/brand/leaf-logo.svg'
import './WoodworkLogo.css'

export type WoodworkLogoVariant = 'leaf' | 'branch'

export type WoodworkLogoSize = 'accent' | 'nav' | 'section' | 'hero' | 'watermark'

type WoodworkLogoProps = Omit<SVGProps<SVGSVGElement>, 'ref'> & {
  readonly variant: WoodworkLogoVariant
  readonly size?: WoodworkLogoSize
}

const aspectRatio: Record<WoodworkLogoVariant, number> = {
  leaf: 214 / 138,
  branch: 253 / 256
}

const heights: Record<WoodworkLogoSize, number> = {
  accent: 22,
  nav: 28,
  section: 56,
  hero: 96,
  watermark: 140
}

const defaultSize: Record<WoodworkLogoVariant, WoodworkLogoSize> = {
  leaf: 'accent',
  branch: 'hero'
}

const art: Record<WoodworkLogoVariant, FC<SVGProps<SVGSVGElement>>> = {
  leaf: LeafLogoArt,
  branch: BranchLogoArt
}

const WoodworkLogo: FC<WoodworkLogoProps> = ({
  variant,
  size,
  className = '',
  style,
  ...props
}) => {
  const resolvedSize = size ?? defaultSize[variant]
  const height = heights[resolvedSize]
  const width = Math.round(height * aspectRatio[variant])
  const decorative = resolvedSize === 'watermark' || props['aria-hidden'] === true
  const LogoArt = art[variant]

  return (
    <LogoArt
      width={width}
      height={height}
      focusable='false'
      className={`woodwork-logo woodwork-logo--${variant} woodwork-logo--${resolvedSize} ${className}`.trim()}
      style={{ width, height, ...style }}
      aria-hidden={decorative ? true : undefined}
      role={decorative ? 'presentation' : 'img'}
      aria-label={decorative ? undefined : 'Tristan J. Hall woodworking mark'}
      {...props}
    />
  )
}

export default WoodworkLogo
